import { useEffect } from "react";
import { getSearchRevealers } from "./searchRevealRegistry";

/**
 * Cmd/Ctrl+F → focus the search box of the current view.
 *
 * The browser's native find bar is useless in this app: guest/table rows are
 * virtualised and most lists filter on the server, so "find on page" searches a
 * few dozen rendered rows out of thousands. The real search sits in the
 * toolbar. This maps the reflex onto it.
 *
 * Contract, in order:
 *  1. Collect every visible `[data-search-input]`.
 *  2. Pick the one in the topmost layer (highest ancestor z-index, ties broken
 *     by document order) — an open modal's search beats the page toolbar's.
 *  3. Never pull focus DOWN a layer: if the focused element sits higher than
 *     the best candidate, bail. Otherwise Cmd/Ctrl+F inside an overlay with no
 *     search box would fling focus into the page behind it, which `useFocusTrap`
 *     then yanks back on teardown.
 *  4. Found → preventDefault + focus + select (typing replaces, like the native
 *     find bar).
 *  5. None visible → ask the reveal registry (advanced-mode toolbars mount
 *     their search box lazily) and retry for a few frames.
 *  6. Still nothing → do NOT preventDefault. The native find bar opens exactly
 *     as before. The shortcut never takes something away without giving
 *     something back.
 */

const SEARCH_INPUT_SELECTOR = "[data-search-input]";

/** How many animation frames to wait for React to mount a revealed input. */
const REVEAL_FRAMES = 3;

/**
 * Highest z-index across the positioned ancestors of `el`.
 *
 * A deliberate approximation of the real stacking order: it ignores
 * opacity/transform stacking contexts, but every overlay in this app is a
 * portal with an explicit z-index class (Modal `z-[900]`, AI rail `z-10000`,
 * dropdown portals), so ancestor-max ranks them correctly without any overlay
 * having to opt in.
 */
function layerScore(el: Element | null): number {
  let score = 0;
  let node: Element | null = el;
  while (node && node !== document.documentElement) {
    const style = getComputedStyle(node);
    if (style.position !== "static") {
      const z = Number.parseInt(style.zIndex, 10);
      if (Number.isFinite(z) && z > score) score = z;
    }
    node = node.parentElement;
  }
  return score;
}

/**
 * Deliberately does NOT use `getClientRects()` / `offsetParent`: jsdom has no
 * layout, so those report everything as invisible and the whole feature would
 * be untestable. Checking the display/visibility chain covers what actually
 * occurs here — hidden search boxes are unmounted, not laid out at zero size.
 *
 * Zero opacity is NOT treated as hidden on purpose: the advanced-mode toolbars
 * animate in from `opacity: 0`, and the reveal path must be able to focus the
 * input on the very first frame.
 */
function isFocusableSearchInput(el: Element): el is HTMLInputElement {
  if (!(el instanceof HTMLInputElement)) return false;
  if (el.disabled || el.readOnly || el.hidden) return false;

  let node: HTMLElement | null = el;
  while (node) {
    const style = getComputedStyle(node);
    if (style.display === "none" || style.visibility === "hidden") return false;
    node = node.parentElement;
  }
  return true;
}

/** The visible search input in the topmost layer, or null. */
function findSearchInput(): HTMLInputElement | null {
  const candidates = Array.from(
    document.querySelectorAll(SEARCH_INPUT_SELECTOR),
  ).filter(isFocusableSearchInput);

  if (candidates.length === 0) return null;

  let best = candidates[0];
  let bestScore = layerScore(best);
  for (const candidate of candidates.slice(1)) {
    const score = layerScore(candidate);
    // Ties go to the later element: portals append to <body>, so the most
    // recently opened overlay sits last.
    if (score >= bestScore) {
      best = candidate;
      bestScore = score;
    }
  }

  // Never drag focus out of a higher layer (see contract step 3).
  if (layerScore(document.activeElement) > bestScore) return null;

  return best;
}

function focusSearchInput(input: HTMLInputElement): void {
  input.focus();
  // Select rather than append: matches the native find bar, where typing
  // immediately starts a fresh query.
  input.select();
}

/** A revealer whose screen is currently on-screen, topmost first. */
function findRevealer() {
  const usable = getSearchRevealers().filter((r) => {
    const node = r.getNode();
    return node !== null && isVisibleNode(node);
  });
  if (usable.length === 0) return null;

  return usable.reduce((best, current) =>
    layerScore(current.getNode()) >= layerScore(best.getNode()) ? current : best,
  );
}

function isVisibleNode(node: HTMLElement): boolean {
  let current: HTMLElement | null = node;
  while (current) {
    const style = getComputedStyle(current);
    if (style.display === "none" || style.visibility === "hidden") return false;
    current = current.parentElement;
  }
  return true;
}

/** Retry across a few frames — the input only exists after React commits. */
function focusAfterReveal(framesLeft: number): void {
  requestAnimationFrame(() => {
    const input = findSearchInput();
    if (input) {
      focusSearchInput(input);
      return;
    }
    if (framesLeft > 1) focusAfterReveal(framesLeft - 1);
  });
}

export function useSearchHotkey(): void {
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.repeat || e.altKey || e.shiftKey) return;
      if (!(e.metaKey || e.ctrlKey)) return;
      if (e.key.toLowerCase() !== "f") return;

      const input = findSearchInput();
      if (input) {
        e.preventDefault();
        focusSearchInput(input);
        return;
      }

      const revealer = findRevealer();
      if (revealer) {
        // Swallow up front: a registered revealer means this view HAS a search,
        // it is just collapsed. preventDefault cannot be deferred to the async
        // retry below.
        e.preventDefault();
        revealer.reveal();
        focusAfterReveal(REVEAL_FRAMES);
        return;
      }

      // No search anywhere → leave the native find bar alone.
    };

    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, []);
}
